/**
 * Geographical component module.
 * @module shared/components/Geographical
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Row from '../Row';
import {withSize} from 'react-sizeme';
import Column from '../Column';
import {ColorBox} from '../../utils/colors';
import highchartsMap from 'highcharts/modules/map';
import proj4 from 'proj4';
import mapDataIE from './world.json';
import LinkedText from '../LinkedText';
import {Table} from '../../index';
import {v4 as uuid} from 'uuid';

if (typeof window !== 'undefined') {
  window.proj4 = window.proj4 || proj4;
}
highchartsMap(Highcharts);

const Geographical = ({
  title,
  data,
  formattedData,
  sliceAction,
  hideLegend,
  size,
  table,
  // toolTipFormatter,
}) => {
  const options = React.useMemo(() => ({
    title: false,
    key: uuid(),
    chart: {
      backgroundColor: 'rgba(0, 0, 0, 0.00)',
      marginTop: 10,
      spacingTop: 0,
      marginBottom: 10,
      spacingBottom: 0,
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        data: data,
        mapData: mapDataIE,
        joinBy: 'code',
        name: 'Exposure Percent',
        point: {
          events: {
            click: sliceAction && (slice => sliceAction(slice.point)),
            mouseOver: function onMouseOver(e) {
              if (e.target.value) {
                e.target.graphic.element.style.cursor = 'pointer';
              }
            },
          },
        },
        states: {
          hover: {
            enabled: false,
            brightness: 0,
          },
        },
      },
    ],
  }), [data]);

  let layout = 'sm';
  let Direction = Row;
  let InnerDirection = Row;
  let containerHeight = '100%';
  let containerWidth = '100%';
  if (size.height >= 300) {
    if (size.width > 620) {
      Direction = Column;
      containerHeight = '100%';
      containerWidth = '100%';
      layout = 'lg';
    } else if (size.width > 380) {
      Direction = Row;
      InnerDirection = Row;
      containerHeight = '100%';
      containerWidth = '100%';
      layout = 'md';
    } else {
      Direction = Row;
      InnerDirection = Column;
      layout = 'md';
    }
  } else if (size.width >= 600) {
    Direction = Row;
    containerHeight = '100%';
    containerWidth = '170px';
    layout = 'lg';
  } else if (size.width >= 300) {
    Direction = Row;
    InnerDirection = Column;
    containerHeight = '100%';
    containerWidth = '170px';
    layout = 'md';
  }
  return <Direction height={'100%'} width={'100%'}>
    <Column height={containerHeight} maxHeight={'190px'} width={containerWidth}>
      <HighchartsReact
        key={options.key.concat(size.width)}
        constructorType={'mapChart'}
        containerProps={{style: {height: '100%', width: '100%'}}}
        highcharts={Highcharts}
        options={options}
      />
    </Column>
    {layout === 'md' && !hideLegend && <InnerDirection width={'fit-content'}
                                                       layout={InnerDirection === Column ? null : {grow: 1}}
                                                       wrap={true}
                                                       justifySubRow={'space around'}
                                                       alignSubRow={InnerDirection === Column && 'left'}
                                                       alignItems={InnerDirection === Column && 'left'}>
      {formattedData.slice(0, 4).filter(i => !!i)
        .map(i => <div key={`${title}-${i.name}`}>
          <LinkedText colorize={false} fontSize={'12px'} verticalAlign={true} action={sliceAction && (() => sliceAction(i))}>
            <ColorBox color={i.color}/>
            {i.name}
          </LinkedText>
        </div>)}
    </InnerDirection>}
    {table && layout === 'lg' && !hideLegend && <Column height={Direction === Row ? '100%' : null}
                                                        width={Direction === Row ? '50px' : null}
                                                        layout={{grow: 1}}>
      <Table height={'50px'}
             dataSet={formattedData.filter(d => !!d[table.columns[0].dataPoint])}
             defaultSort={table.defaultSort}
             rowOnClick={sliceAction}
             columns={table.columns}/></Column>}
  </Direction>;

  // let layout = 'sm';
  // let Direction = Column;
  // let InnerDirection = Row;
  // let containerHeight = '100px';
  // let containerWidth = '100%';
  // if (size.width > 600) {
  //   Direction = Row;
  //   InnerDirection = Column;
  //   containerHeight = '100%';
  //   containerWidth = '100px';
  //   layout = 'md';
  // } else if (size.height > 340) {
  //   layout = 'md';
  // }
  //
  // return <Direction key={`geo-${data.length}`} layout={{grow: 1}} height={'100px'}>
  //   <Column layout={{grow: 1}} height={containerHeight} maxHeight={'190px'} width={containerWidth}>
  //     <HighchartsReact
  //       constructorType={'mapChart'}
  //       containerProps={{style: {height: '100%', width: '100%'}}}
  //       highcharts={Highcharts}
  //       options={options}
  //     />
  //   </Column>
  //   <Column width={'auto'}>
  //     {!hideLegend && layout !== 'sm' && <InnerDirection wrap={true}
  //                                                        justifySubRow={'space around'}
  //                                                        alignSubRow={InnerDirection === Column && 'left'}
  //                                                        alignItems={InnerDirection === Column && 'left'}>
  //       {data.slice(0, 4).map(i => <div key={`${title}-${i.name}`} onClick={() => sliceAction(i)}>
  //         <LinkedText colorize={false} fontSize={'12px'} action={sliceAction && (() => sliceAction(i))}>
  //           <ColorBox color={i.color}/>
  //           {i.name}
  //         </LinkedText>
  //       </div>)}
  //     </InnerDirection>}
  //   </Column>
  // </Direction>;
};

export default withSize({monitorHeight: true, monitorWidth: true})(Geographical);

Geographical.propTypes = {
  toolTipFormatter: PropTypes.func,
  title: PropTypes.string,
  /**
   * From SizeMe
   */
  size: PropTypes.object,
  // Y-Axis Scale
  // Number of Y-Axis Markers
  // X-Axis Scale
  // Number of X-Axis Markers
  /**
   * Optional function to call on click of a slice
   */
  sliceAction: PropTypes.func,
  /**
   * Data formatted for the graph
   */
  data: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Data formatted for the tab
   */
  formattedData: PropTypes.arrayOf(PropTypes.shape({})),
  hideLegend: PropTypes.bool,
  /**
   * Forwarded to table
   */
  table: PropTypes.shape({
    defaultSort: PropTypes.object,
    columns: PropTypes.array,
  }),
};
